/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Button,
  Card,
  FormControlLabel,
  Grid,
  Slider,
  Switch,
  TextField,
  Typography
} from '@material-ui/core'
import React, { ChangeEvent, useEffect, useState } from 'react'
import { useParams } from 'react-router'
import { useGlobalState } from '../../providers/GlobalState'
import { IolLibrary } from '../../types/graphql'
import { SuccessButton, WarningButton } from '../Buttons'
import { ButtonBox } from '../Help/HelpLayout'
import { useIolForm } from './useIolForm'
import { useDebouncedCallback } from 'use-debounce'
import NumberFormat from 'react-number-format'
import { useApolloClient } from '@apollo/client'
import { REMOVE_IOL_CUSTOMIZATIONS, UPDATE_IOL } from '../../graphql/queries'
import omit from 'lodash.omit'
import styled from 'styled-components'

const AmountCustomInput = (props: any) => (
  <TextField label="Valor unitário (cada olho)" variant="outlined" {...props} />
)

const Content = styled.div`
  display: flex;
  flex-direction: column;
  height: 90%;
  overflow: auto;
  overflow-x: hidden;
`
interface ParamTypes {
  libId: string
  type?: 'copiar'
}

const IolLibSlug = (): JSX.Element => {
  const { libId, type } = useParams<ParamTypes>()
  const { iolLibraries, refetch } = useGlobalState()
  const client = useApolloClient()
  const [loading, setLoading] = useState(false)

  const {
    data: storeData,
    setData: setStoreData,
    reset,
    setIsEditing
  } = useIolForm()
  const [data, setData] = React.useState<IolLibrary>(storeData)

  const debouncedSetData = useDebouncedCallback(setStoreData, 100)
  useEffect(() => {
    debouncedSetData(data)
  }, [data])

  useEffect(() => {
    const lib = iolLibraries.find(lib => lib?.slug === libId)
    if (!lib) return
    setIsEditing(true)
    setData(lib)
    return () => {
      reset()
    }
  }, [libId, iolLibraries])

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value, name } = e.currentTarget

    if (name === 'price') {
      setData({ ...data, price: { float: Number(value) } })
    } else {
      setData({ ...data, [name]: value })
    }
  }

  const handlePropertiesChange = (
    propertyKey: keyof Omit<IolLibrary['properties'], '__typename'>,
    key: string,
    value: number | number[]
  ) => {
    let newProperties: any = { ...data.properties }
    newProperties = newProperties[propertyKey].map((property: any) => {
      if (property?.key === key) return { ...property, value: value as number }
      return property
    })
    setData({
      ...data,
      properties: {
        ...data.properties,
        [propertyKey]: newProperties
      }
    })
  }

  const handleItemsChange = (key: string, checked: boolean) => {
    const newProperties = data.properties.items.map(property => {
      if (property?.key === key) return { ...property, hasProperty: checked }
      return property
    })
    setData({
      ...data,
      properties: {
        ...data.properties,
        items: newProperties
      }
    })
  }

  const handleCancel = () => {
    const lib = iolLibraries.find(lib => lib?.slug === libId)
    if (!lib) return
    setData(lib)
  }

  const handleRemove = () => {
    setLoading(true)
    client
      .mutate({
        mutation: REMOVE_IOL_CUSTOMIZATIONS,
        variables: {
          id: data.id
        }
      })
      .then(() => {
        refetch?.()
        setLoading(false)
      })
  }

  const handleSave = () => {
    setLoading(true)
    const id = data.id
    client
      .mutate({
        mutation: UPDATE_IOL,
        variables: {
          id,
          input: {
            price: data.price.float,
            name: data.name,
            enable: data.enable,
            position: +data.position,
            internalAnnotations: data.internalAnnotations,
            properties: {
              graphics: data.properties.graphics.map(g =>
                omit(g, ['__typename', 'name'])
              ),
              items: data.properties.items.map(g =>
                omit(g, ['__typename', 'name'])
              ),
              satisfaction: data.properties.satisfaction.map(g =>
                omit(g, ['__typename', 'name'])
              )
            }
          }
        }
      })
      .then(() => {
        refetch?.()
        setLoading(false)
      })
  }

  return (
    <Card
      style={{
        position: 'absolute',
        width: 'calc(75% - 20px)',
        background: 'rgba(255,255,255,0.80)',
        height: 'calc(90% - 200px)',
        padding: '16px 16px',
        overflowY: 'auto',
        borderRadius: 4,
        zIndex: 20,
        top: 100,
        left: '50%',
        transform: 'translate(-50%)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start'
      }}
    >
      <Content>
        <Typography variant="h4">Edição de lente {data.name}</Typography>
        <br />
        <TextField
          type="text"
          name="name"
          value={`${data?.name}${type === 'copiar' ? '-copia' : ''}`}
          variant="outlined"
          label="Nome Comercial"
          fullWidth
          onChange={handleChange}
        />
        <br />
        <FormControlLabel
          control={
            <Switch
              checked={data.enable}
              name="enable"
              color="primary"
              onChange={(e, checked) =>
                handleChange({
                  currentTarget: { value: checked, name: 'enable' }
                } as any)
              }
            />
          }
          label="Ativo a exibição para pacientes"
        />
        <br />
        <NumberFormat
          style={{ width: '100%' }}
          prefix="R$ "
          thousandSeparator="."
          decimalSeparator=","
          decimalScale={2}
          fixedDecimalScale
          value={data?.price?.float}
          onValueChange={values =>
            handleChange({
              currentTarget: {
                name: 'price',
                value: values.floatValue?.toString()
              }
            } as any)
          }
          allowNegative={false}
          allowedDecimalSeparators={['.', ',']}
          customInput={AmountCustomInput}
        />
        <br />
        <Grid container justify="space-around" spacing={2}>
          <Grid item xs={12}>
            <Typography component="h2" variant="subtitle1" align="left">
              Gráficos
            </Typography>
          </Grid>
          {data.properties.graphics.map(graph => (
            <Grid
              item
              xs={12}
              md={4}
              key={graph?.key}
              justify="space-between"
              style={{ display: 'flex', flexDirection: 'column' }}
            >
              <span>{graph?.name}</span>
              <Slider
                name={graph?.key}
                value={graph?.value}
                valueLabelDisplay="auto"
                onChange={(_e, value) =>
                  handlePropertiesChange('graphics', graph?.key || '', value)
                }
              />
            </Grid>
          ))}
          <Grid item xs={12}>
            <Typography component="h2" variant="subtitle1" align="left">
              Itens
            </Typography>
          </Grid>
          {data.properties.items.map(item => (
            <Grid item xs={6} md={4} key={item?.key}>
              <FormControlLabel
                control={
                  <Switch
                    id={item?.key}
                    checked={item?.hasProperty}
                    onChange={(_e, checked) =>
                      handleItemsChange(item?.key || '', checked)
                    }
                    name={item?.key}
                    color="primary"
                  />
                }
                label={item?.name}
              />
            </Grid>
          ))}
          <Grid item xs={12}>
            <Typography component="h2" variant="subtitle1" align="left">
              Satisfação
            </Typography>
          </Grid>
          {data.properties.satisfaction.map(sat => (
            <Grid
              item
              xs={12}
              md={4}
              key={sat?.key}
              justify="space-between"
              style={{ display: 'flex', flexDirection: 'column' }}
            >
              <span>{sat?.name}</span>
              <Slider
                name={sat?.key}
                value={sat?.value}
                valueLabelDisplay="auto"
                onChange={(_e, value) =>
                  handlePropertiesChange('satisfaction', sat?.key || '', value)
                }
              />
            </Grid>
          ))}
        </Grid>
        <br />
        <TextField
          type="number"
          name="position"
          inputProps={{
            min: 0
          }}
          value={data.position}
          onChange={handleChange}
          variant="outlined"
          label="Posição"
          helperText="Informe um numero 0 a 1000 quanto menor o numero mais ao topo da lista ira aparecer"
        />
        <br />
        <TextField
          name="internalAnnotations"
          onChange={handleChange}
          value={data.internalAnnotations || ''}
          label="Anotações internas"
          variant="outlined"
          helperText="Esse campo é apenas de anotações internas, não será exibido no APP"
          multiline
          fullWidth
          rows={2}
        />
        <br />
      </Content>
      <ButtonBox
        style={{
          paddingTop: 20
        }}
      >
        <Button
          color="primary"
          onClick={handleRemove}
          variant="contained"
          disabled={loading}
        >
          {loading ? 'Aguarde...' : 'Valor Padrão'}
        </Button>
        <WarningButton variant="contained" onClick={handleCancel}>
          Cancelar
        </WarningButton>
        <SuccessButton
          disabled={loading}
          variant="contained"
          onClick={handleSave}
        >
          {loading ? 'Aguarde...' : 'Salvar'}
        </SuccessButton>
      </ButtonBox>
    </Card>
  )
}

export default IolLibSlug
